import React, { createContext, useContext, useState, useEffect, useCallback, useMemo } from 'react';

/**
 * TabMemoryContext - Remembers the last active subtab for each main navigation page
 *
 * This context stores and persists the last visited subtab for each main navigation item,
 * so when users switch between main pages and come back, they return to the same subtab
 * they were on before.
 */

const TabMemoryContext = createContext();

export const useTabMemory = () => {
  const context = useContext(TabMemoryContext);
  if (!context) {
    throw new Error('useTabMemory must be used within TabMemoryProvider');
  }
  return context;
};

export function TabMemoryProvider({ children }) {
  // Initialize from localStorage or use defaults
  const [tabMemory, setTabMemory] = useState(() => {
    const stored = localStorage.getItem('tabMemory');
    return stored ? JSON.parse(stored) : {};
  });

  // Save to localStorage whenever tabMemory changes
  useEffect(() => {
    localStorage.setItem('tabMemory', JSON.stringify(tabMemory));
  }, [tabMemory]);

  /**
   * Remember the active tab for a specific page
   * @param {string} pageName - The name of the page (e.g., 'customer', 'production')
   * @param {string} tabId - The ID of the active tab
   */
  const rememberTab = useCallback((pageName, tabId) => {
    setTabMemory(prev => ({
      ...prev,
      [pageName]: tabId
    }));
  }, []);

  /**
   * Get the last remembered tab for a specific page
   * @param {string} pageName - The name of the page
   * @param {string} defaultTab - The default tab to use if none is remembered
   * @returns {string} The tab ID to use
   */
  const getRememberedTab = useCallback((pageName, defaultTab = 'overview') => {
    return tabMemory[pageName] || defaultTab;
  }, [tabMemory]);

  /**
   * Clear the memory for a specific page or all pages
   * @param {string} pageName - Optional page name to clear, if not provided clears all
   */
  const clearMemory = useCallback((pageName = null) => {
    if (pageName) {
      setTabMemory(prev => {
        const newMemory = { ...prev };
        delete newMemory[pageName];
        return newMemory;
      });
    } else {
      setTabMemory({});
    }
  }, []);

  const value = useMemo(() => ({
    rememberTab,
    getRememberedTab,
    clearMemory,
    tabMemory // Expose the raw memory object for debugging
  }), [rememberTab, getRememberedTab, clearMemory, tabMemory]);

  return (
    <TabMemoryContext.Provider value={value}>
      {children}
    </TabMemoryContext.Provider>
  );
}